// Channel routing defaults for every instrument/element in the stage designer.
// Each entry defines the channels that are auto-generated when an artist places
// that element. Artists can override every field.

export type MicType = 'Condenser' | 'Dynamic' | 'DI' | 'Ribbon' | 'Other';
export type SuppliedBy = 'artist' | 'venue' | 'either';

export interface DefaultChannel {
  channelName: string;   // e.g. "Piano L", "Kick", "Vocal"
  micType: MicType;
  typicalMic?: string;   // e.g. "SM57", "DPA 4099", "AKG D112"
  suppliedBy: SuppliedBy;
  notes?: string;
}

export interface InstrumentChannelDef {
  channels: DefaultChannel[];
  notes?: string;        // general note shown alongside the generated rows
}

// ── Lookup table ──────────────────────────────────────────────────────────────
// Key = element id as used in StageDesigner

const def = (channels: DefaultChannel[], notes?: string): InstrumentChannelDef =>
  ({ channels, notes });

const ch = (
  channelName: string,
  micType: MicType,
  typicalMic?: string,
  suppliedBy: SuppliedBy = 'venue',
  notes?: string,
): DefaultChannel => ({ channelName, micType, typicalMic, suppliedBy, notes });

export const CHANNEL_DEFAULTS: Record<string, InstrumentChannelDef> = {

  // ── Standalone mic elements (placed independently on stage) ─────────────────
  'vocal-mic': def([
    ch('Vocal', 'Dynamic', 'SM58', 'venue'),
  ]),
  'instrument-mic': def([
    ch('Instrument Mic', 'Dynamic', 'SM57', 'venue'),
  ]),
  'di-box': def([
    ch('DI', 'DI', undefined, 'venue'),
  ]),

  // ── Keyboards ────────────────────────────────────────────────────────────────
  'grand-piano': def([
    ch('Piano L', 'Condenser', undefined, 'venue'),
    ch('Piano R', 'Condenser', undefined, 'venue'),
  ], 'Stereo condenser pair above the soundboard. Artist may have preference on placement.'),

  'upright-piano': def([
    ch('Piano L', 'Condenser', undefined, 'venue'),
    ch('Piano R', 'Condenser', undefined, 'venue'),
  ], 'Condensers inside lid or above strings. Often a single mono mic for smaller rooms.'),

  'electric-piano': def([
    ch('Keys L', 'DI', undefined, 'venue'),
    ch('Keys R', 'DI', undefined, 'venue'),
  ]),

  'organ': def([
    ch('Organ L', 'DI', undefined, 'venue'),
    ch('Organ R', 'DI', undefined, 'venue'),
  ], 'If a Leslie cabinet is present, mic instead of DI — confirm with artist.'),

  'rhodes': def([
    ch('Rhodes L', 'DI', undefined, 'venue'),
    ch('Rhodes R', 'DI', undefined, 'venue'),
  ]),

  // ── Guitars / Bass ───────────────────────────────────────────────────────────
  'acoustic-guitar': def([
    ch('Guitar Mic', 'Condenser', undefined, 'venue'),
    ch('Guitar DI', 'DI', undefined, 'venue'),
  ], 'Blend of mic and DI is standard. Artist may prefer mic-only or DI-only.'),

  'electric-guitar': def([
    ch('Guitar', 'Dynamic', 'SM57', 'venue'),
  ], 'Mic on amp cabinet. Confirm if artist is running direct from modeller (needs DI instead).'),

  'acoustic-bass': def([
    ch('Bass DI', 'DI', undefined, 'venue'),
    ch('Bass Mic', 'Condenser', undefined, 'venue'),
  ], 'DI + mic blend is standard. Confirm pickup type with artist.'),

  'electric-bass': def([
    ch('Bass DI', 'DI', undefined, 'venue'),
  ], 'Direct from instrument or bass amp DI out.'),

  // ── Drums ────────────────────────────────────────────────────────────────────
  'drum-kit': def([
    ch('Kick', 'Dynamic', 'AKG D112', 'venue'),
    ch('Snare', 'Dynamic', 'SM57', 'venue'),
    ch('OH L', 'Condenser', undefined, 'venue'),
    ch('OH R', 'Condenser', undefined, 'venue'),
    ch('Hi-Hat', 'Condenser', undefined, 'venue'),
  ], 'Minimum 5-piece overhead setup. Add tom mics, room mics or extra snare if rider requires.'),

  // ── Brass ────────────────────────────────────────────────────────────────────
  'trumpet': def([
    ch('Trumpet', 'Condenser', undefined, 'venue'),
  ], 'Condenser or dynamic on bell. Artist may request a specific mic.'),

  'flugelhorn': def([
    ch('Flugelhorn', 'Condenser', undefined, 'venue'),
  ]),

  'cornet': def([
    ch('Cornet', 'Dynamic', 'SM57', 'venue'),
  ]),

  'trombone': def([
    ch('Trombone', 'Dynamic', 'SM57', 'venue'),
  ]),

  'bass-trombone': def([
    ch('Bass Trombone', 'Dynamic', undefined, 'venue'),
  ]),

  'french-horn': def([
    ch('French Horn', 'Condenser', undefined, 'venue'),
  ], 'Bell faces away — mic behind or to the side of the bell.'),

  'tuba': def([
    ch('Tuba', 'Dynamic', undefined, 'venue'),
  ]),

  // ── Reed / Woodwind ───────────────────────────────────────────────────────────
  'alto-sax': def([
    ch('Alto Sax', 'Condenser', undefined, 'venue'),
  ]),

  'tenor-sax': def([
    ch('Tenor Sax', 'Condenser', undefined, 'venue'),
  ]),

  'baritone-sax': def([
    ch('Bari Sax', 'Condenser', undefined, 'venue'),
  ]),

  'soprano-sax': def([
    ch('Soprano Sax', 'Condenser', undefined, 'venue'),
  ]),

  'clarinet': def([
    ch('Clarinet', 'Condenser', undefined, 'venue'),
  ]),

  'bass-clarinet': def([
    ch('Bass Clarinet', 'Condenser', undefined, 'venue'),
  ]),

  'flute': def([
    ch('Flute', 'Condenser', undefined, 'venue'),
  ], 'Condensers work best pointing across the embouchure, not at the mouthpiece.'),

  // ── Strings ───────────────────────────────────────────────────────────────────
  'violin': def([
    ch('Violin', 'Condenser', 'DPA 4099', 'either'),
  ], 'Many violinists bring their own clip-on mic (e.g. DPA 4099). Confirm before allocating venue mic.'),

  'viola': def([
    ch('Viola', 'Condenser', 'DPA 4099', 'either'),
  ], 'Many viola players bring their own clip-on. Confirm before allocating venue mic.'),

  'cello': def([
    ch('Cello', 'Condenser', undefined, 'venue'),
  ], 'Clip-on or stand-mounted condenser near f-hole.'),

  'double-bass': def([
    ch('Bass DI', 'DI', undefined, 'venue'),
    ch('Bass Mic', 'Condenser', undefined, 'venue'),
  ], 'Pickup DI + mic blend is standard. Confirm if arco or pizzicato dominant — affects mic placement.'),

  // ── Mallet / Percussion ───────────────────────────────────────────────────────
  'vibraphone': def([
    ch('Vibe L', 'Condenser', undefined, 'venue'),
    ch('Vibe R', 'Condenser', undefined, 'venue'),
  ], 'Stereo overhead pair.'),

  'marimba': def([
    ch('Marimba L', 'Condenser', undefined, 'venue'),
    ch('Marimba R', 'Condenser', undefined, 'venue'),
  ], 'Stereo overhead pair — instrument is wide, space mics accordingly.'),

  'xylophone': def([
    ch('Xylophone', 'Condenser', undefined, 'venue'),
  ]),

  'congas': def([
    ch('Conga Hi', 'Dynamic', undefined, 'venue'),
    ch('Conga Lo', 'Dynamic', undefined, 'venue'),
  ]),

  'bongos': def([
    ch('Bongos', 'Dynamic', undefined, 'venue'),
  ]),

  'timbales': def([
    ch('Timbale Hi', 'Dynamic', undefined, 'venue'),
    ch('Timbale Lo', 'Dynamic', undefined, 'venue'),
  ]),

  'percussion': def([
    ch('Perc L', 'Condenser', undefined, 'venue'),
    ch('Perc R', 'Condenser', undefined, 'venue'),
  ], 'General overhead pair. Adjust based on what the percussionist brings.'),

  // ── Modern / Electronic ───────────────────────────────────────────────────────
  'synthesizer': def([
    ch('Synth L', 'DI', undefined, 'venue'),
    ch('Synth R', 'DI', undefined, 'venue'),
  ]),

  'sampler': def([
    ch('Sampler L', 'DI', undefined, 'venue'),
    ch('Sampler R', 'DI', undefined, 'venue'),
  ]),

  'electronic-wind': def([
    ch('EWI', 'DI', undefined, 'venue'),
  ], 'Electronic wind instrument — direct output only.'),

  // ── Vocal ─────────────────────────────────────────────────────────────────────
  'vocals': def([
    ch('Vocal', 'Dynamic', 'SM58', 'venue', 'Artist may supply own microphone — confirm before allocating.'),
  ]),

  // ── Generic catch-alls (one per instrument family) ────────────────────────────
  'generic-horn': def([
    ch('Horn', 'Dynamic', 'SM57', 'venue'),
  ], 'Generic brass instrument. Edit channel name and mic type to suit.'),

  'generic-reed': def([
    ch('Reed', 'Condenser', undefined, 'venue'),
  ], 'Generic woodwind / reed instrument. Edit channel name and mic type to suit.'),

  'generic-string': def([
    ch('String', 'Condenser', undefined, 'either'),
  ], 'Generic string instrument. Artist may supply their own mic — confirm first.'),

  'generic-keyboard': def([
    ch('Keys L', 'DI', undefined, 'venue'),
    ch('Keys R', 'DI', undefined, 'venue'),
  ], 'Generic keyboard. Confirm whether stereo or mono output.'),

  'generic-percussion': def([
    ch('Perc L', 'Condenser', undefined, 'venue'),
    ch('Perc R', 'Condenser', undefined, 'venue'),
  ], 'Generic percussion. Edit to match actual instrument.'),

  'generic-electronic': def([
    ch('Electronic L', 'DI', undefined, 'venue'),
    ch('Electronic R', 'DI', undefined, 'venue'),
  ], 'Generic electronic instrument. Confirm output configuration with artist.'),

  'generic-vocal': def([
    ch('Vocal', 'Dynamic', 'SM58', 'venue'),
  ], 'Generic vocal. Artist may supply own mic.'),

  'custom': def([], 'Custom instrument — no default channels. Add channels manually.'),
};
